import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AnnouncementBody from './AnnouncementBody';

/* The body field of an announcement form - HR and Entrepreneur both use it.
 *
 * The textarea on its own was already markdown, but nothing in the form said
 * how to make a link beyond a line of small print, and there was no way to see
 * whether it had worked short of posting. So: an Insert link button that writes
 * the markdown at the cursor, and a preview that renders exactly what the
 * reader will get (same AnnouncementBody component, same link handling).
 *
 * The caret move after an insert has to wait for the parent's state to come
 * back down as a new value, hence setting it from an effect rather than inline.
 */

const URL_PLACEHOLDER = 'https://';
const LABEL_PLACEHOLDER = 'link text';

const toolbarBtn = (color) => ({
  background: 'white', color, border: `1px solid ${color}`, borderRadius: 6,
  padding: '2px 10px', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
});

export default function AnnouncementBodyEditor({
  value, onChange, textareaStyle, placeholder = 'Body (markdown supported)',
  accentColor = '#0069a6', hintColor = '#58585b',
}) {
  const taRef = useRef(null);
  const pendingSel = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const sel = pendingSel.current;
    if (!sel || !taRef.current) return;
    pendingSel.current = null;
    taRef.current.focus();
    taRef.current.setSelectionRange(sel[0], sel[1]);
  });

  const insertLink = () => {
    const text = value || '';
    const ta = taRef.current;
    const start = ta ? ta.selectionStart : text.length;
    const end = ta ? ta.selectionEnd : text.length;
    const selected = text.slice(start, end);
    const label = selected || LABEL_PLACEHOLDER;

    onChange(`${text.slice(0, start)}[${label}](${URL_PLACEHOLDER})${text.slice(end)}`);

    // Selected some words? They become the link text, so leave the caret on the
    // URL. Nothing selected? Select the placeholder label instead so the next
    // keystroke replaces it.
    const urlStart = start + label.length + 3;   // "[" + label + "]("
    pendingSel.current = selected
      ? [urlStart, urlStart + URL_PLACEHOLDER.length]
      : [start + 1, start + 1 + label.length];
  };

  return (
    <>
      <textarea
        ref={taRef}
        style={textareaStyle}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', margin: '0.35rem 0' }}>
        <button type="button" style={toolbarBtn(accentColor)} onClick={insertLink}>
          <i className="bi bi-link-45deg" />Insert link
        </button>
        <button type="button" style={toolbarBtn(hintColor)} onClick={() => setShowPreview((v) => !v)}>
          <i className={`bi ${showPreview ? 'bi-pencil' : 'bi-eye'}`} />
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
        <span style={{ fontSize: '0.7rem', color: hintColor }}>
          Markdown: **bold**, *italic*, [text](https://…), lists with - or 1. A pasted web
          address becomes a link on its own. Paste C2P Central course links exactly as they
          appear in the address bar - readers are sent via its login page so they land on the
          lesson, not its home page.
        </span>
      </div>
      {showPreview && (
        <div style={{ border: '1px dashed rgba(10,41,71,0.25)', borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.5rem', background: 'white' }}>
          <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1px', color: hintColor, marginBottom: '0.3rem' }}>
            Preview
          </div>
          {value ? (
            <AnnouncementBody style={{ color: hintColor, fontSize: '0.85rem', lineHeight: 1.55 }} linkColor={accentColor}>
              {value}
            </AnnouncementBody>
          ) : (
            <span style={{ color: hintColor, fontSize: '0.8rem', fontStyle: 'italic' }}>Nothing to preview yet.</span>
          )}
        </div>
      )}
    </>
  );
}

AnnouncementBodyEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  textareaStyle: PropTypes.object,
  placeholder: PropTypes.string,
  accentColor: PropTypes.string,
  hintColor: PropTypes.string,
};
